import { forwardRef, useMemo } from 'react';
import { Box } from '@material-ui/core';
import { spacing, palette } from '@material-ui/system';

import styled from 'hooks/styled';

const getTextStyles = ({ theme, variant, view }) => {
  const variantDesktop = variant;
  const variantMobile = `${variant}-mobile`;

  const stylesDesktop = {
    ...theme.text[variantDesktop],
  };
  const stylesMobile = {
    ...stylesDesktop,
    ...theme.text[variantMobile],
  };

  if (view === 'desktop') {
    return stylesDesktop;
  }
  if (view === 'mobile') {
    return stylesMobile;
  }
  return {
    ...stylesDesktop,

    [theme.breakpoints.down('xs')]: {
      ...stylesMobile,
    },
  };
};

const Root = styled(Box, {
  label: 'Text',
  ignore: [
    'view',
    'variant',
    'color',
    'cursor',
    'lineBreak',
    'lineHeight',
    'fontWeight',
    'fontSize',
    'fontStyle',
    'letterSpacing',
    'textTransform',
    'spacingProps',
    'wordBreak',
    'verticalAlign',
    'lines',
    'uppercase',
  ],
})(
  ({
    theme,
    view,
    variant,
    color,
    cursor,
    lineBreak,
    lineHeight,
    fontWeight,
    fontSize,
    fontStyle,
    letterSpacing,
    textTransform,
    spacingProps,
    wordBreak,
    verticalAlign,
    lines,
    uppercase,
  }) => {
    const styles = variant === 'inherit' ? {} : { ...theme.text.base };

    const textStyles = getTextStyles({ theme, variant, view });
    const linesStyles =
      Number.isFinite(lines) && lines > 0
        ? {
            display: '-webkit-inline-box',
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            WebkitLineClamp: lines,
          }
        : {};

    const overwrites = Object.entries({
      lineHeight,
      fontWeight,
      fontSize,
      letterSpacing,
      fontStyle,
      lineBreak,
      wordBreak,
      verticalAlign,
      textTransform,
      cursor,
    }).reduce((res, [name, value]) => {
      return !value
        ? res
        : {
            ...res,
            [name]: value,
          };
    }, {});

    return {
      textTransform: uppercase ? 'uppercase' : undefined,
      ...styles,
      ...textStyles,
      color: palette({ theme, color }).color,
      ...linesStyles,
      ...overwrites,
      ...spacing({ theme, ...spacingProps }),
    };
  },
);

const spacingKeys = [
  'm',
  'mt',
  'mb',
  'ml',
  'mr',
  'mx',
  'my',
  'p',
  'pt',
  'pb',
  'pl',
  'pr',
  'px',
  'py',
];

const Text = forwardRef((props, ref) => {
  const {
    cursor,
    className,
    variant = 'body1-regular',
    color = 'inherit',
    view = 'auto',
    uppercase,
    lineHeight,
    fontWeight,
    fontSize,
    fontStyle,
    letterSpacing,
    textTransform,
    lineBreak,
    wordBreak,
    lines,
    verticalAlign,
    component: initialComponent,
    ...rest
  } = props;

  const [spacingProps, restProps] = useMemo(() => {
    const sp = {};
    const rp = {};

    Object.entries(rest).forEach(([name, value]) => {
      if (spacingKeys.includes(name)) {
        sp[name] = value;
        return;
      }
      rp[name] = value;
    });
    return [sp, rp];
  }, [rest]);

  const component = useMemo(() => {
    if (initialComponent) {
      return initialComponent;
    }
    const prefix = variant.slice(0, 6);

    switch (prefix) {
      case 'title2':
        return 'h2';
      case 'title3':
        return 'h3';
      case 'title4':
        return 'h4';
      case 'title5':
        return 'h5';
      case 'title1':
      default:
        return undefined;
    }
  }, [initialComponent, variant]);

  return (
    <Root
      ref={ref}
      component={component}
      className={className}
      view={view}
      variant={variant}
      color={color}
      cursor={cursor}
      lineBreak={lineBreak}
      lineHeight={lineHeight}
      fontWeight={fontWeight}
      fontSize={fontSize}
      fontStyle={fontStyle}
      letterSpacing={letterSpacing}
      textTransform={textTransform}
      spacingProps={spacingProps}
      wordBreak={wordBreak}
      verticalAlign={verticalAlign}
      lines={lines}
      uppercase={uppercase}
      {...restProps}
    />
  );
});

export default Text;
